import { DeleteFilled, PlusCircleFilled } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, CardProps, Form, List, Popover, Space } from 'antd';
import Card from 'app/components/Card';
import Select from 'app/components/Select';
import { useLazyGetInspectorAvailableQuery } from 'app/features/inspection/api';
import { get } from 'lodash';
import moment, { Moment } from 'moment';
import React from 'react';
import { DerivedText } from './DerivedText';
import SelectBox from 'app/components/Select/SelectBox';

const StyledCard = styled(Card)`
  border-radius: 4px;
  border: 1px solid var(--text-text-200, #c4c8d0);
  background: #fff;
  margin-bottom: 16px;
  .ant-card-head {
    padding: 0;
    .ant-card-head-title {
      padding: 8px;
      color: var(--text-text-600, #232b3a);
      font-size: 16px;
      font-style: normal;
      font-weight: 500;
      line-height: 19px; /* 118.75% */
      letter-spacing: 0.08px;
    }
  }
  .ant-card-body {
    padding: 8px;
  }
`;

const StyledButton = styled(Button)`
  padding-left: 0;
`;

const StyledDerivedText = styled(DerivedText)`
  color: var(--text-text-600, #232b3a);
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 14px; /* 116.667% */
  letter-spacing: 0.06px;
`;

const WrapperItem = styled(Space)`
  width: 100%;
  justify-content: space-between;
  .ant-space-item {
    .ant-form-item {
      margin-bottom: 0;
    }
    &:nth-child(1) {
      width: 90px;
    }
    &:nth-child(2) {
      width: 120px;
    }
  }
`;
const generateManDayOptions = (min = 0.1, max = 1.5) => {
  const options = [];
  for (let i = min; i <= max; i += 0.1) {
    options.push({
      value: parseFloat(i.toFixed(1)),
      label: `${+i.toFixed(1)} ${i > 1 ? 'days' : 'day'}`
    });
  }
  options.push({
    value: max,
    label: `${max} days`
  });
  return options;
};

const MAN_DAY_OPTIONS = generateManDayOptions();

// const MAN_DAY_OPTIONS = [
//   {
//     value: 0.1,
//     label: '0.1 day'
//   },
//   {
//     value: 0.2,
//     label: '0.2 day'
//   },
//   {
//     value: 0.3,
//     label: '0.3 day'
//   },
//   // {
//   //   value: 0.25,
//   //   label: '0.25 day'
//   // },
//   {
//     value: 0.5,
//     label: '0.5 day'
//   },
//   {
//     value: 1,
//     label: '1 day'
//   },
//   {
//     value: 1.5,
//     label: '1.5 days'
//   }
// ];

type Props = {
  title?: string;
  cardProps?: CardProps;
  name: string | number | (string | number)[];
  startDate: Moment | null | undefined;
  endDate: Moment | null | undefined;
  assigned: any[];
  events: any[];
  jobManday: number;
};

export const PickerInspector: React.FC<Props> = ({
  title,
  name,
  startDate,
  assigned,
  jobManday,
  events = []
}) => {
  const [visibleSelectInspector, setVisibleSelectInspector] =
    React.useState(false);
  const [fetchInspectorAvailable, result] = useLazyGetInspectorAvailableQuery();

  const namePath = React.useMemo(() => {
    if (Array.isArray(name)) return [...name];
    return [name];
  }, [name]);

  const availableInspectorOptions = React.useMemo(() => {
    const data = get(result.data, 'list', []) || [];
    return data.map((inspector: any) => {
      return {
        value: inspector.id,
        label: `${inspector.firstName} ${inspector.lastName}`,
        ...inspector
      };
    });
  }, [result.data]);

  const getResources = () => {
    const currentEvents = events.filter((event) => {
      return (
        moment(event.start).format('YYYY-MM-DD') ===
        startDate?.format('YYYY-MM-DD')
      );
    });
    if (currentEvents && currentEvents.length) {
      return currentEvents.reduce((acc, { resource }) => {
        const { inspectorName, manDay } = resource;
        const fixedManday = parseFloat((manDay || 0).toFixed(2));
        if (acc[inspectorName]) {
          acc[inspectorName] = Number(
            parseFloat(
              `${((acc[inspectorName] || 0) + (fixedManday || 0)).toFixed(2)}`
            )
          );
        } else {
          acc[inspectorName] = Number(fixedManday);
        }
        return acc;
      }, {});
    }
    return null;
  };

  const isInspectorAvailable = (inspector) => {
    const resource = getResources();
    if (resource) {
      const manday = resource[`${inspector.firstName} ${inspector.lastName}`];
      return (manday || 0) < 1.5;
    }
    return true;
  };

  return (
    <StyledCard bordered={false} title={title ? title : undefined}>
      <Form.List name={[...namePath, 'inspectors']}>
        {(fields, { add, remove }) => {
          return (
            <Space direction="vertical">
              <List
                dataSource={fields}
                renderItem={(field, index) => {
                  return (
                    <List.Item>
                      <WrapperItem align="start">
                        <Form.Item name={[field.name, 'inspector']}>
                          <StyledDerivedText
                            formatText={(inspector) => {
                              return inspector.label;
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          rules={[
                            {
                              required: true,
                              message: 'Manday is required'
                            }
                          ]}
                          name={[field.name, 'manDay']}
                        >
                          <Select options={MAN_DAY_OPTIONS}></Select>
                        </Form.Item>
                        <Button
                          onClick={() => {
                            remove(index);
                          }}
                          type="text"
                          danger
                          icon={<DeleteFilled />}
                        ></Button>
                      </WrapperItem>
                    </List.Item>
                  );
                }}
                footer={
                  <Popover
                    destroyTooltipOnHide
                    placement="topLeft"
                    trigger={['click']}
                    open={visibleSelectInspector}
                    onOpenChange={(visible) => {
                      if (visible) {
                        //When Popover show call api to get available inspectors.
                        fetchInspectorAvailable({
                          current: 1,
                          pageSize: 99999,
                          startDate: startDate.format('YYYY-MM-DD'),
                          endDate: startDate.format('YYYY-MM-DD')
                        });
                      }
                      setVisibleSelectInspector(visible);
                    }}
                    showArrow={false}
                    content={
                      <SelectBox
                        options={availableInspectorOptions?.filter(
                          (inspector) => {
                            return isInspectorAvailable(inspector);
                          }
                        )}
                        multiple
                        type="checkbox"
                        value={assigned.map(({ inspector }) => inspector)}
                        onApply={(selections) => {
                          const willAdd = selections.reduce(
                            (acc: any[], selection) => {
                              const existed = assigned.find(
                                (value) => value?.inspector?.id === selection.id
                              );
                              if (!existed) {
                                acc.push(selection);
                              }

                              return acc;
                            },
                            []
                          );

                          willAdd.forEach((selection, index) => {
                            if (
                              jobManday <= 1 &&
                              index === 0 &&
                              (!assigned || !assigned.length)
                            ) {
                              const manDayOption = MAN_DAY_OPTIONS.find(
                                ({ value }) => value === jobManday
                              );
                              add({
                                inspector: selection,
                                manDay: manDayOption?.value
                              });
                            } else {
                              add({
                                inspector: selection,
                                manDay: null
                              });
                            }
                          });
                          setVisibleSelectInspector(false);
                        }}
                      />
                    }
                  >
                    <StyledButton
                      onClick={() => {
                        setVisibleSelectInspector(true);
                      }}
                      key="add"
                      icon={<PlusCircleFilled />}
                      type="link"
                    >
                      Add Inspector
                    </StyledButton>
                  </Popover>
                }
              ></List>
            </Space>
          );
        }}
      </Form.List>
    </StyledCard>
  );
};
